import { useGameStore } from '@/stores';
import { useUIStore } from '@/stores/uiStore';
import { Team } from '@/types/player';
import { CardType } from '@/types/card';
import { PositionCalculator, type PlayerPosition } from '@/game/utils/PositionCalculator';
import './PlayerStatusOverlay.css';

/**
 * Maximum life points per team.
 */
const MAX_LIFE_POINTS: Record<Team, number> = {
  [Team.TEAM_1]: 60,
  [Team.TEAM_2]: 80,
};

/**
 * Role label mapping based on game spec.
 * - Local player → 'Player'
 * - SOLO mode (local = Team 2): the other two are 'COP1', 'COP2'
 * - DUO mode (local = Team 1): the Team 1 partner → 'COP2', the opposing Team 2 → 'Burglar'
 */
const resolvePlayerLabels = (
  players: Array<{ id: string; team: Team }>,
  localPlayerId: string
): Record<string, string> => {
  const labels: Record<string, string> = {};
  const localPlayer = players.find((p) => p.id === localPlayerId);
  if (!localPlayer) return labels;

  labels[localPlayerId] = 'Player';

  if (localPlayer.team === Team.TEAM_2) {
    // Local is the SOLO side. The opponents are Team 1 (two cops).
    const team1Players = players.filter((p) => p.team === Team.TEAM_1);
    team1Players.forEach((p, idx) => {
      labels[p.id] = idx === 0 ? 'COP1' : 'COP2';
    });
  } else {
    // Local is the DUO side. Partner = COP2, opposing solo = Burglar.
    players.forEach((p) => {
      if (p.id === localPlayerId) return;
      if (p.team === Team.TEAM_1) {
        labels[p.id] = 'COP2';
      } else {
        labels[p.id] = 'Burglar';
      }
    });
  }

  return labels;
};

/**
 * Get the character image filename for a player based on their team and
 * their position within Team 1 (to assign different cop images).
 * team1Index: 0 → cop.png, 1+ → cop2.png. Ignored for Team 2.
 */
const getCharacterImage = (team: Team, team1Index: number): string => {
  if (team !== Team.TEAM_1) return '/assets/characters/burglar.png';
  return team1Index === 0 ? '/assets/characters/cop.png' : '/assets/characters/cop2.png';
};

/**
 * Get the bar fill color based on the remaining HP percentage.
 */
const getHealthColor = (percentage: number): string => {
  if (percentage > 0.6) return '#4ade80';
  if (percentage > 0.3) return '#fbbf24';
  return '#ef4444';
};

/**
 * Slot-position class name suffix used by CSS.
 */
const positionToClass = (position: PlayerPosition): string => {
  return `PlayerStatusOverlay__slot--${position}`;
};

interface SlotData {
  playerId: string;
  playerName: string;
  characterImage: string;
  team: Team;
  position: PlayerPosition;
  isTurn: boolean;
  isNext: boolean;
  drawPenalty?: 2 | 4;
  /** ホバープレビュー: 「もし出したら次のターンになるプレイヤー」を示す */
  isPreviewNext: boolean;
  /** ホバープレビュー: 「もし出したらペナルティを受けるプレイヤー」の付与枚数 */
  previewDrawPenalty?: 2 | 4;
  lifePoints: number;
  maxLifePoints: number;
}

/**
 * 「もしこのカードを出したら次のターンになるプレイヤー」のインデックスを計算
 * - SKIP: (curr + 2*direction) % N。Solo Team 2 (N=3) が出すなら自分に戻る（変則ルール、GameStateManager.ts:219 と一致）
 * - REVERSE: (curr - direction) % N（3人プレイでは方向反転のみ）
 */
const calculatePreviewNextIndex = (
  cardType: CardType,
  currentIndex: number,
  direction: 1 | -1,
  playerCount: number,
  currentPlayerTeam: Team
): number | null => {
  if (cardType !== CardType.SKIP && cardType !== CardType.REVERSE) {
    return null;
  }
  const N = playerCount;

  if (cardType === CardType.SKIP) {
    let nextIndex = (currentIndex + 2 * direction + N * 2) % N;
    // Solo Team 2 の SKIP 変則: 3人プレイで Team 2 が SKIP を出すと自分に戻る
    if (currentPlayerTeam === Team.TEAM_2 && N === 3) {
      nextIndex = currentIndex;
    }
    return nextIndex;
  }

  // REVERSE: 方向反転後の次プレイヤー
  const reversedDirection = (-direction) as 1 | -1;
  return (currentIndex + reversedDirection + N) % N;
};

/**
 * 「もしこのカードを出したらペナルティを受けるプレイヤー」のインデックスを計算
 * DRAW_TWO / WILD_DRAW_FOUR は通常の次プレイヤー（SKIP変則は適用されない）
 */
const calculatePreviewPenaltyIndex = (
  cardType: CardType,
  currentIndex: number,
  direction: 1 | -1,
  playerCount: number
): number | null => {
  if (cardType !== CardType.DRAW_TWO && cardType !== CardType.WILD_DRAW_FOUR) {
    return null;
  }
  return (currentIndex + direction + playerCount) % playerCount;
};

/**
 * PlayerStatusOverlay
 * Renders a floating overlay for each player showing the character icon,
 * the role label, the HP bar, and turn/next/penalty badges.
 */
const PlayerStatusOverlay = () => {
  const gameState = useGameStore((state) => state.gameState);
  const matchState = useGameStore((state) => state.matchState);
  const localPlayerId = useGameStore((state) => state.localPlayerId);
  const cardHoverPreview = useUIStore((state) => state.cardHoverPreview);

  if (!gameState || !matchState || !localPlayerId) {
    return null;
  }

  const { players, currentPlayerIndex, direction, drawCount, drawPenaltyType } = gameState;
  const { lifePoints } = matchState;

  if (players.length === 0) {
    return null;
  }

  const localPlayerIndex = players.findIndex((p) => p.id === localPlayerId);
  if (localPlayerIndex === -1) {
    return null;
  }

  const localPlayerTeam = players[localPlayerIndex].team;
  const playerTeams = players.map((p) => p.team);
  const labels = resolvePlayerLabels(players, localPlayerId);

  const nextIndex =
    (currentPlayerIndex + direction + players.length) % players.length;

  const penaltyAmount: 2 | 4 | undefined =
    drawPenaltyType === 'DRAW_TWO'
      ? 2
      : drawPenaltyType === 'WILD_DRAW_FOUR'
        ? 4
        : undefined;

  // ホバープレビュー: ローカルプレイヤーが現在のターン中の場合のみ反映
  // （cardHoverPreview は CardSprite 側で「自分の手札 & プレイ可能」のみセットされるが、
  //   stale 防止のため購読側でも防御的にチェックする）
  const isLocalTurn =
    players[currentPlayerIndex] !== undefined &&
    String(players[currentPlayerIndex].id) === String(localPlayerId);

  const currentPlayerTeam = players[currentPlayerIndex]?.team ?? Team.TEAM_1;

  const previewNextIndex =
    cardHoverPreview && isLocalTurn
      ? calculatePreviewNextIndex(
          cardHoverPreview.cardType,
          currentPlayerIndex,
          direction,
          players.length,
          currentPlayerTeam
        )
      : null;

  const previewPenaltyIndex =
    cardHoverPreview && isLocalTurn
      ? calculatePreviewPenaltyIndex(
          cardHoverPreview.cardType,
          currentPlayerIndex,
          direction,
          players.length
        )
      : null;

  const team1Ids = players.filter((p) => p.team === Team.TEAM_1).map((p) => p.id);

  const slots: SlotData[] = players.map((player, idx) => {
    const position = PositionCalculator.getPlayerPosition(
      idx,
      players.length,
      localPlayerIndex,
      { localPlayerTeam, playerTeams }
    );

    const isTurn = idx === currentPlayerIndex;
    // Show penalty badge on the player who is about to receive it (current player).
    const showPenalty = isTurn && drawCount > 0 && penaltyAmount !== undefined;

    // プレビュー: SKIP/REVERSE の予告対象（自分のターンの予告は許容: SKIP変則ルールで自分に戻る）
    const isPreviewNext = previewNextIndex !== null && idx === previewNextIndex;

    // プレビュー: Draw 系の対象。既にペナルティバッジが出ている枠とは重複させない（視覚的混乱回避）
    const previewDrawPenalty: 2 | 4 | undefined =
      previewPenaltyIndex !== null && idx === previewPenaltyIndex && !showPenalty
        ? cardHoverPreview?.drawAmount
        : undefined;

    return {
      playerId: player.id,
      playerName: labels[player.id] ?? player.name,
      characterImage: getCharacterImage(player.team, team1Ids.indexOf(player.id)),
      team: player.team,
      position,
      isTurn,
      isNext: idx === nextIndex && idx !== currentPlayerIndex,
      drawPenalty: showPenalty ? penaltyAmount : undefined,
      isPreviewNext,
      previewDrawPenalty,
      lifePoints: lifePoints[player.id] ?? 0,
      maxLifePoints: MAX_LIFE_POINTS[player.team],
    };
  });

  return (
    <div className="PlayerStatusOverlay" aria-hidden="false">
      {slots.map((slot) => {
        const percentage = Math.max(
          0,
          Math.min(1, slot.lifePoints / slot.maxLifePoints)
        );
        const fillColor = getHealthColor(percentage);

        const isWanted = slot.team === Team.TEAM_2;
        const frameSrc = isWanted
          ? '/assets/redesign-v3/hud/frame-wanted.png'
          : '/assets/redesign-v3/hud/frame-dossier.png';

        return (
          <div
            key={slot.playerId}
            className={`PlayerStatusOverlay__slot ${positionToClass(slot.position)} ${
              slot.isTurn ? 'PlayerStatusOverlay__slot--turn' : ''
            } ${slot.isPreviewNext ? 'PlayerStatusOverlay__slot--preview-next' : ''} PlayerStatusOverlay__slot--${
              isWanted ? 'wanted' : 'dossier'
            }`}
          >
            {/* フレーム PNG (装飾レイヤ。pure black 部分は screen ブレンドで透過) */}
            <img
              src={frameSrc}
              alt=""
              aria-hidden="true"
              className="PlayerStatusOverlay__frame"
              draggable={false}
            />

            {slot.isNext && (
              <span className="PlayerStatusOverlay__next-badge">NEXT</span>
            )}
            {slot.isPreviewNext && !slot.isNext && (
              <span
                className="PlayerStatusOverlay__preview-next-badge"
                aria-label="次のターン予告"
              >
                NEXT?
              </span>
            )}

            <div className="PlayerStatusOverlay__character">
              <img
                src={slot.characterImage}
                alt={slot.team === Team.TEAM_1 ? 'Cop' : 'Burglar'}
                className="PlayerStatusOverlay__character-img"
                draggable={false}
              />
              {slot.drawPenalty !== undefined && (
                <span className="PlayerStatusOverlay__penalty-badge">
                  +{slot.drawPenalty}
                </span>
              )}
              {slot.previewDrawPenalty !== undefined && (
                <span
                  className="PlayerStatusOverlay__preview-penalty-badge"
                  aria-label={`ドロー予告 +${slot.previewDrawPenalty}`}
                >
                  +{slot.previewDrawPenalty}
                </span>
              )}
            </div>

            <div className="PlayerStatusOverlay__name">{slot.playerName}</div>

            <div
              className="PlayerStatusOverlay__hp-bar"
              role="progressbar"
              aria-valuenow={slot.lifePoints}
              aria-valuemin={0}
              aria-valuemax={slot.maxLifePoints}
              aria-label={`${slot.playerName} HP: ${slot.lifePoints}/${slot.maxLifePoints}`}
            >
              <div
                className="PlayerStatusOverlay__hp-bar-fill"
                style={{
                  width: `${percentage * 100}%`,
                  backgroundColor: fillColor,
                }}
              />
              <span className="PlayerStatusOverlay__hp-text">
                {Math.max(0, slot.lifePoints)} / {slot.maxLifePoints}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PlayerStatusOverlay;
